import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Handshake,
  Instagram,
  LayoutGrid,
  Megaphone,
  Users,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ScrollProgress } from "@/components/ScrollProgress";
import { CookieBanner } from "@/components/CookieBanner";
import { InfoTip } from "@/components/InfoTip";
import { CARD_SPONSOR } from "@/data/media";
import { JoinForm } from "@/components/JoinForm";
import { PartnersGrid } from "@/components/PartnersGrid";
import { SOCIAL } from "@/data/sponsors";
import { StoryInvite } from "@/components/HomeSections";
import { useSeo } from "@/hooks/useSeo";

const COSA_TROVERAI = [
  {
    icon: LayoutGrid,
    titolo: "La vetrina delle aziende",
    testo:
      "Ogni azienda aderente avrà il proprio spazio: logo, settore, una riga di presentazione e il link diretto al sito.",
    tip: "La vetrina viene pubblicata solo con i dati e i loghi autorizzati dalle aziende partner.",
  },
  {
    icon: BadgeCheck,
    titolo: "I livelli di adesione",
    testo:
      "Main Partner, Gold, Partner e Sostenitore: livelli diversi di visibilità, dallo spazio in evidenza alla presenza in elenco.",
    tip: "I livelli definiscono ampiezza dello spazio, contenuti social dedicati e presenza agli incontri.",
  },
  {
    icon: Megaphone,
    titolo: "Il racconto social",
    testo:
      "Per ogni nuova azienda pubblicheremo una presentazione dedicata sul profilo Instagram della rete.",
    tip: "Un contenuto concordato con l'azienda, con tag reciproco per raggiungere entrambe le community.",
  },
  {
    icon: Users,
    titolo: "L'incontro con le socie",
    testo:
      "Le aziende partner entrano nei momenti della rete: incontri, tavoli di lavoro e progetti condivisi con le imprenditrici.",
    tip: "Un canale diretto verso professioniste e imprese guidate da donne di ogni settore.",
  },
];

export default function SponsorPage() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 250);
  }, [hash]);

  useSeo({
    title: "Sponsor e aziende partner — DonnaImpresa",
    description:
      "La pagina dedicata alle aziende che sostengono DonnaImpresa: come funzionerà la vetrina sponsor, i livelli di adesione e la visibilità social. Candida la tua azienda.",
    path: "/sponsor",
  });

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="sponsor-page">
      <ScrollProgress />
      <Navbar />

      <main>
        <section className="grain relative overflow-hidden pt-36 sm:pt-40">
          <div className="pointer-events-none absolute -right-24 top-24 h-[320px] w-[320px] rounded-full bg-[#E0708A]/20 blur-[110px] di-blob" />
          <div className="relative mx-auto grid max-w-[1400px] items-center gap-10 px-5 pb-12 sm:px-10 lg:grid-cols-[1.05fr_0.95fr] lg:pb-16">
            <div>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-[#6B5E62] di-underline"
                data-testid="sponsor-back-home"
              >
                <ArrowLeft size={14} /> Home
              </Link>
              <motion.h1
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="mt-8 font-display text-4xl leading-[1.03] tracking-tight text-[#1A1617] sm:text-5xl lg:text-6xl"
                data-testid="sponsor-title"
              >
                Le aziende che
                <br />
                <span className="di-shine italic">sosterranno la rete</span>.
              </motion.h1>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-[#6B5E62]">
                Questa pagina ospiterà le aziende partner di DonnaImpresa.
                Stiamo costruendo la rete con cura: qui compariranno soltanto
                realtà che aderiscono davvero al progetto, con i loro loghi e i
                loro riferimenti ufficiali.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/sponsor#aderisci"
                  data-testid="sponsor-cta-form"
                  className="group inline-flex items-center gap-2 rounded-full bg-[#5E2A52] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#E0708A]"
                >
                  Candida la tua azienda
                  <ArrowRight
                    size={16}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
                <a
                  href={SOCIAL.instagram}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="sponsor-instagram-link"
                  className="inline-flex items-center gap-2 text-sm text-[#5E2A52] di-underline"
                >
                  <Instagram size={15} /> Seguici su Instagram
                </a>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-[28px] border border-[#E8E2E4] shadow-[0_24px_70px_rgba(94,42,82,0.18)]"
            >
              <img
                src={CARD_SPONSOR}
                alt="Composizione astratta dedicata alle aziende partner di DonnaImpresa"
                className="h-[280px] w-full object-cover sm:h-[400px]"
                loading="lazy"
              />
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-5 py-14 sm:px-10 lg:py-20">
          <h2 className="font-display text-3xl tracking-tight text-[#1A1617] sm:text-4xl">
            Cosa troverai in questa pagina
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2" data-testid="sponsor-features">
            {COSA_TROVERAI.map((c, i) => (
              <motion.article
                key={c.titolo}
                data-testid={`sponsor-feature-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: (i % 2) * 0.08 }}
                className="rounded-2xl border border-[#E8E2E4] bg-white p-7 shadow-[0_8px_32px_rgba(94,42,82,0.05)] transition-transform duration-300 hover:-translate-y-1 sm:p-8"
              >
                <c.icon size={22} className="text-[#E0708A]" />
                <h3 className="mt-5 flex items-center gap-2 font-display text-2xl text-[#1A1617]">
                  {c.titolo}
                  <InfoTip text={c.tip} testId={`tip-sponsor-page-${i}`} />
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#6B5E62]">
                  {c.testo}
                </p>
              </motion.article>
            ))}
          </div>

          <div className="mt-16" data-testid="partners-section">
            <h2 className="font-display text-3xl tracking-tight text-[#1A1617] sm:text-4xl">
              Le aziende partner
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#6B5E62]">
              Le realtà che sostengono concretamente la rete. Nessun logo di
              fantasia: qui compaiono solo le aziende che hanno aderito.
            </p>
            <PartnersGrid />
          </div>
        </section>
        <JoinForm />
        <StoryInvite />
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
}
